import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { toast } from 'sonner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, Pencil, Trash2, ArrowLeft } from 'lucide-react';

const Catalog = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isMounted = useRef(false);

  const [mainGroups, setMainGroups] = useState([]);
  const [products, setProducts] = useState([]);
  const [goldTypes, setGoldTypes] = useState([]);
  const [diamondColours, setDiamondColours] = useState([]);
  const [diamondClarities, setDiamondClarities] = useState([]);
  const [diamondSizes, setDiamondSizes] = useState([]);
  const [diamondShapes, setDiamondShapes] = useState([]);
  const [colourStoneCategories, setColourStoneCategories] = useState([]);
  const [colourStoneNames, setColourStoneNames] = useState([]);
  const [beadPearlCategories, setBeadPearlCategories] = useState([]);
  const [beadPearlNames, setBeadPearlNames] = useState([]);

  // Lead Reasons state (grouped: [{category, reasons:[{id,reason}]}])
  const [lostReasons, setLostReasons] = useState([]);
  const [parkedReasons, setParkedReasons] = useState([]);
  const [wonReasons, setWonReasons] = useState([]);

  // Lead reason dialogs
  const [reasonDialog, setReasonDialog] = useState(null); // {mode:'add-reason'|'edit-reason'|'add-category'|'rename-category', type, data}
  const [reasonForm, setReasonForm] = useState({ category: '', reason: '', old_name: '' });
  const [deleteReasonTarget, setDeleteReasonTarget] = useState(null); // {id, reason, type}
  const [loading, setLoading] = useState(true);

  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Form states
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'main-groups');

  // Sync active tab → URL silently (bypasses React Router history so back nav is unaffected)
  useEffect(() => {
    if (!isMounted.current) { isMounted.current = true; return; }
    const url = new URL(window.location.href);
    if (activeTab && activeTab !== 'main-groups') url.searchParams.set('tab', activeTab); else url.searchParams.delete('tab');
    window.history.replaceState(window.history.state, '', url.toString());
  }, [activeTab]);
  const [itemName, setItemName] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [deletingItem, setDeletingItem] = useState(null);

  const REASON_TABS = ['lost-reasons', 'parked-reasons', 'won-reasons'];
  const reasonTypeMap = { 'lost-reasons': 'lost', 'parked-reasons': 'parked', 'won-reasons': 'won' };

  const fetchLeadReasons = async () => {
    const [lost, parked, won] = await Promise.all([
      api.get('/crm/lead-reasons/lost').catch(() => ({ data: [] })),
      api.get('/crm/lead-reasons/parked').catch(() => ({ data: [] })),
      api.get('/crm/lead-reasons/won').catch(() => ({ data: [] })),
    ]);
    setLostReasons(lost.data);
    setParkedReasons(parked.data);
    setWonReasons(won.data);
  };

  const reasonDataMap = { 'lost-reasons': lostReasons, 'parked-reasons': parkedReasons, 'won-reasons': wonReasons };

  const handleSaveReason = async () => {
    const leadType = reasonTypeMap[activeTab];
    try {
      if (reasonDialog?.mode === 'add-reason') {
        await api.post('/crm/lead-reasons', { type: leadType, category: reasonForm.category, reason: reasonForm.reason });
        toast.success('Reason added');
      } else if (reasonDialog?.mode === 'edit-reason') {
        await api.put(`/crm/lead-reasons/${reasonDialog.data.id}`, { reason: reasonForm.reason, category: reasonForm.category });
        toast.success('Reason updated');
      } else if (reasonDialog?.mode === 'rename-category') {
        await api.put('/crm/lead-reason-categories', { type: leadType, old_name: reasonForm.old_name, new_name: reasonForm.category });
        toast.success('Category renamed');
      }
      setReasonDialog(null);
      setReasonForm({ category: '', reason: '', old_name: '' });
      await fetchLeadReasons();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
  };

  const handleDeleteReason = async () => {
    if (!deleteReasonTarget) return;
    try {
      await api.delete(`/crm/lead-reasons/${deleteReasonTarget.id}`);
      toast.success('Reason deleted');
      setDeleteReasonTarget(null);
      await fetchLeadReasons();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const fetchCatalogData = async () => {
    try {
      setLoading(true);
      const [mainGroupsRes, productsRes, goldTypesRes, diaColoursRes, diaClaritiesRes, diaSizesRes,
             csCategRes, csNamesRes, bpCategRes, bpNamesRes, diaShapesRes] = await Promise.all([
        api.get('/catalog/main-groups'),
        api.get('/catalog/products'),
        api.get('/catalog/gold-types'),
        api.get('/catalog/diamond-colours'),
        api.get('/catalog/diamond-clarities'),
        api.get('/catalog/diamond-sizes'),
        api.get('/catalog/colour-stone-categories'),
        api.get('/catalog/colour-stone-names'),
        api.get('/catalog/bead-pearl-categories'),
        api.get('/catalog/bead-pearl-names'),
        api.get('/catalog/diamond-shapes'),
      ]);
      setMainGroups(mainGroupsRes.data);
      setProducts(productsRes.data);
      setGoldTypes(goldTypesRes.data);
      setDiamondColours(diaColoursRes.data);
      setDiamondClarities(diaClaritiesRes.data);
      setDiamondSizes(diaSizesRes.data);
      setColourStoneCategories(csCategRes.data);
      setColourStoneNames(csNamesRes.data);
      setBeadPearlCategories(bpCategRes.data);
      setBeadPearlNames(bpNamesRes.data);
      setDiamondShapes(diaShapesRes.data);
    } catch (error) {
      console.error('Error fetching catalog:', error);
      toast.error('Failed to load catalog data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = async () => {
    if (!itemName.trim()) {
      toast.error('Please enter a name');
      return;
    }

    try {
      const endpointMap = {
        'main-groups': '/catalog/main-groups',
        'products': '/catalog/products',
        'gold-types': '/catalog/gold-types',
        'diamond-colours': '/catalog/diamond-colours',
        'diamond-clarities': '/catalog/diamond-clarities',
        'diamond-sizes': '/catalog/diamond-sizes',
        'diamond-shapes': '/catalog/diamond-shapes',
        'colour-stone-categories': '/catalog/colour-stone-categories',
        'colour-stone-names': '/catalog/colour-stone-names',
        'bead-pearl-categories': '/catalog/bead-pearl-categories',
        'bead-pearl-names': '/catalog/bead-pearl-names',
      };
      const endpoint = endpointMap[activeTab];

      const typeMap = {
        'main-groups': 'main_group', 'products': 'product', 'gold-types': 'gold_type',
        'diamond-colours': 'diamond_colour', 'diamond-clarities': 'diamond_clarity', 'diamond-sizes': 'diamond_size',
        'diamond-shapes': 'diamond_shape',
        'colour-stone-categories': 'colour_stone_category', 'colour-stone-names': 'colour_stone_name',
        'bead-pearl-categories': 'bead_pearl_category', 'bead-pearl-names': 'bead_pearl_name',
      };
      await api.post(endpoint, {
        type: typeMap[activeTab],
        name: itemName.trim(),
      });

      toast.success(`${itemType} added successfully`);
      setShowAddDialog(false);
      setItemName('');
      fetchCatalogData();
    } catch (error) {
      console.error('Error adding item:', error);
      toast.error(error.response?.data?.detail || 'Failed to add item');
    }
  };

  const handleEditItem = async () => {
    if (!itemName.trim()) {
      toast.error('Please enter a name');
      return;
    }

    try {
      const endpointMap = {
        'main-groups': `/catalog/main-groups/${editingItem.id}`,
        'products': `/catalog/products/${editingItem.id}`,
        'gold-types': `/catalog/gold-types/${editingItem.id}`,
        'diamond-colours': `/catalog/diamond-colours/${editingItem.id}`,
        'diamond-clarities': `/catalog/diamond-clarities/${editingItem.id}`,
        'diamond-sizes': `/catalog/diamond-sizes/${editingItem.id}`,
        'diamond-shapes': `/catalog/diamond-shapes/${editingItem.id}`,
        'colour-stone-categories': `/catalog/colour-stone-categories/${editingItem.id}`,
        'colour-stone-names': `/catalog/colour-stone-names/${editingItem.id}`,
        'bead-pearl-categories': `/catalog/bead-pearl-categories/${editingItem.id}`,
        'bead-pearl-names': `/catalog/bead-pearl-names/${editingItem.id}`,
      };
      const endpoint = endpointMap[activeTab];

      await api.put(endpoint, {
        name: itemName.trim(),
      });

      toast.success(`${itemType} updated successfully`);
      setShowEditDialog(false);
      setItemName('');
      setEditingItem(null);
      fetchCatalogData();
    } catch (error) {
      console.error('Error updating item:', error);
      toast.error(error.response?.data?.detail || 'Failed to update item');
    }
  };

  const handleDeleteItem = async () => {
    try {
      const endpointMap = {
        'main-groups': `/catalog/main-groups/${deletingItem.id}`,
        'products': `/catalog/products/${deletingItem.id}`,
        'gold-types': `/catalog/gold-types/${deletingItem.id}`,
        'diamond-colours': `/catalog/diamond-colours/${deletingItem.id}`,
        'diamond-clarities': `/catalog/diamond-clarities/${deletingItem.id}`,
        'diamond-sizes': `/catalog/diamond-sizes/${deletingItem.id}`,
        'diamond-shapes': `/catalog/diamond-shapes/${deletingItem.id}`,
        'colour-stone-categories': `/catalog/colour-stone-categories/${deletingItem.id}`,
        'colour-stone-names': `/catalog/colour-stone-names/${deletingItem.id}`,
        'bead-pearl-categories': `/catalog/bead-pearl-categories/${deletingItem.id}`,
        'bead-pearl-names': `/catalog/bead-pearl-names/${deletingItem.id}`,
      };
      const endpoint = endpointMap[activeTab];

      await api.delete(endpoint);

      toast.success(`${itemType} deleted successfully`);
      setShowDeleteDialog(false);
      setDeletingItem(null);
      fetchCatalogData();
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete item');
    }
  };

  const openAddDialog = () => {
    setItemName('');
    setShowAddDialog(true);
  };

  const openEditDialog = (item) => {
    setEditingItem(item);
    setItemName(item.name);
    setShowEditDialog(true);
  };

  const openDeleteDialog = (item) => {
    setDeletingItem(item);
    setShowDeleteDialog(true);
  };

  const itemsMap = {
    'main-groups': mainGroups, 'products': products, 'gold-types': goldTypes,
    'diamond-colours': diamondColours, 'diamond-clarities': diamondClarities, 'diamond-sizes': diamondSizes,
    'diamond-shapes': diamondShapes,
    'colour-stone-categories': colourStoneCategories, 'colour-stone-names': colourStoneNames,
    'bead-pearl-categories': beadPearlCategories, 'bead-pearl-names': beadPearlNames,
  };
  const typeLabels = {
    'main-groups': 'Main Group', 'products': 'Product', 'gold-types': 'Gold Type',
    'diamond-colours': 'Dia Colour', 'diamond-clarities': 'Dia Clarity', 'diamond-sizes': 'Dia Size',
    'diamond-shapes': 'Dia Shape',
    'colour-stone-categories': 'CS Category', 'colour-stone-names': 'CS Name',
    'bead-pearl-categories': 'B&P Category', 'bead-pearl-names': 'B&P Name',
  };
  const currentItems = itemsMap[activeTab] || [];
  const itemType = typeLabels[activeTab] || 'Item';

  // Fetch data on component mount
  useEffect(() => {
    fetchCatalogData();
    fetchLeadReasons();
  }, []);

  if (!currentUser) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <p className="text-lg">Please log in to access this page</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <p className="text-lg">Loading catalog...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-[#075E54] text-white p-3 sticky top-0 z-10 shadow-md">
        <div className="max-w-6xl mx-auto flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="text-white hover:bg-[#128C7E] flex-shrink-0"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-base sm:text-xl font-semibold truncate">Catalog Management</h1>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto p-4 md:p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <TabsList className="flex w-full max-w-[700px] overflow-x-auto">
              <TabsTrigger value="main-groups" className="text-xs sm:text-sm">Main Groups</TabsTrigger>
              <TabsTrigger value="products" className="text-xs sm:text-sm">Products</TabsTrigger>
              <TabsTrigger value="gold-types" className="text-xs sm:text-sm">Gold Types</TabsTrigger>
              <TabsTrigger value="diamond-colours" className="text-xs sm:text-sm">Dia Colour</TabsTrigger>
              <TabsTrigger value="diamond-clarities" className="text-xs sm:text-sm">Dia Clarity</TabsTrigger>
              <TabsTrigger value="diamond-sizes" className="text-xs sm:text-sm">Dia Size</TabsTrigger>
              <TabsTrigger value="diamond-shapes" className="text-xs sm:text-sm">Dia Shape</TabsTrigger>
              <TabsTrigger value="colour-stone-categories" className="text-xs sm:text-sm whitespace-nowrap">CS Category</TabsTrigger>
              <TabsTrigger value="colour-stone-names" className="text-xs sm:text-sm whitespace-nowrap">CS Name</TabsTrigger>
              <TabsTrigger value="bead-pearl-categories" className="text-xs sm:text-sm whitespace-nowrap">B&P Category</TabsTrigger>
              <TabsTrigger value="bead-pearl-names" className="text-xs sm:text-sm whitespace-nowrap">B&P Name</TabsTrigger>
              <TabsTrigger value="lost-reasons" className="text-xs sm:text-sm whitespace-nowrap text-red-600">Lost Reasons</TabsTrigger>
              <TabsTrigger value="parked-reasons" className="text-xs sm:text-sm whitespace-nowrap text-amber-600">Parked Reasons</TabsTrigger>
              <TabsTrigger value="won-reasons" className="text-xs sm:text-sm whitespace-nowrap text-green-600">Won Reasons</TabsTrigger>
            </TabsList>

            {/* Add button — hidden for reason tabs (they have their own UI) */}
            {!REASON_TABS.includes(activeTab) && (
            <Button onClick={openAddDialog} className="bg-[#25D366] hover:bg-[#20BA5A]">
              <Plus className="h-4 w-4 mr-2" />
              Add {itemType}
            </Button>
            )}
          </div>

          <TabsContent value="main-groups" className="mt-0">
            <div className="bg-white rounded-lg shadow">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {mainGroups.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={2} className="text-center text-gray-500">
                        No main groups found
                      </TableCell>
                    </TableRow>
                  ) : (
                    mainGroups.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell className="font-medium">{item.name}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openEditDialog(item)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openDeleteDialog(item)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="products" className="mt-0">
            <div className="bg-white rounded-lg shadow">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {products.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={2} className="text-center text-gray-500">
                        No products found
                      </TableCell>
                    </TableRow>
                  ) : (
                    products.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell className="font-medium">{item.name}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openEditDialog(item)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openDeleteDialog(item)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="gold-types" className="mt-0">
            <div className="bg-white rounded-lg shadow">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Gold Purity</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {goldTypes.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={2} className="text-center text-gray-500">
                        No gold types found
                      </TableCell>
                    </TableRow>
                  ) : (
                    goldTypes.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell className="font-medium">{item.name}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openEditDialog(item)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openDeleteDialog(item)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Diamond Specs Tabs + Colour Stone + Bead Pearl - reuse same table pattern */}
          {['diamond-colours', 'diamond-clarities', 'diamond-sizes', 'diamond-shapes',
            'colour-stone-categories', 'colour-stone-names',
            'bead-pearl-categories', 'bead-pearl-names'].map(tabKey => {
            const items = itemsMap[tabKey] || [];
            const label = typeLabels[tabKey];
            return (
              <TabsContent key={tabKey} value={tabKey} className="mt-0">
                <div className="bg-white rounded-lg shadow">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{label}</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {items.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={2} className="text-center text-gray-500">
                            No {label.toLowerCase()} entries found
                          </TableCell>
                        </TableRow>
                      ) : (
                        items.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell className="font-medium">{item.name}</TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-2">
                                <Button variant="outline" size="sm" onClick={() => openEditDialog(item)}>
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button variant="outline" size="sm" onClick={() => openDeleteDialog(item)} className="text-red-600 hover:text-red-700">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </TabsContent>
            );
          })}

          {/* Lead Reasons Tabs — category-grouped management */}
          {REASON_TABS.map(tabKey => {
            const leadType = reasonTypeMap[tabKey];
            const groups = reasonDataMap[tabKey] || [];
            const accentColor = leadType === 'lost' ? 'red' : leadType === 'parked' ? 'amber' : 'green';
            const headerClass = leadType === 'lost' ? 'bg-red-50 border-red-200' : leadType === 'parked' ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200';
            const btnClass = leadType === 'lost' ? 'bg-red-600 hover:bg-red-700' : leadType === 'parked' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-green-600 hover:bg-green-700';
            return (
              <TabsContent key={tabKey} value={tabKey} className="mt-0 space-y-3">
                <div className={`rounded-lg border p-3 flex items-center justify-between ${headerClass}`}>
                  <p className="text-sm font-medium text-gray-700">
                    {groups.reduce((acc, g) => acc + g.reasons.length, 0)} reasons across {groups.length} categories
                  </p>
                  <Button size="sm" className={btnClass} onClick={() => {
                    setReasonDialog({ mode: 'add-reason', type: leadType });
                    setReasonForm({ category: groups[0]?.category || '', reason: '', old_name: '' });
                  }}>
                    <Plus className="h-3.5 w-3.5 mr-1" /> Add Reason
                  </Button>
                </div>

                {groups.length === 0 ? (
                  <div className="bg-white rounded-lg border p-8 text-center text-gray-400">No reasons found</div>
                ) : (
                  groups.map(group => (
                    <div key={group.category} className="bg-white rounded-lg border overflow-hidden">
                      {/* Category header */}
                      <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 border-b">
                        <span className="text-sm font-semibold text-gray-700">{group.category}</span>
                        <div className="flex gap-1">
                          <button
                            className="text-xs text-blue-500 hover:text-blue-700 px-2 py-0.5 rounded hover:bg-blue-50 transition-colors"
                            onClick={() => {
                              setReasonDialog({ mode: 'rename-category', type: leadType });
                              setReasonForm({ category: group.category, old_name: group.category, reason: '' });
                            }}
                          >Rename</button>
                          <button
                            className="text-xs text-emerald-600 hover:text-emerald-800 px-2 py-0.5 rounded hover:bg-emerald-50 transition-colors"
                            onClick={() => {
                              setReasonDialog({ mode: 'add-reason', type: leadType });
                              setReasonForm({ category: group.category, reason: '', old_name: '' });
                            }}
                          >+ Add</button>
                        </div>
                      </div>
                      {/* Reasons list */}
                      {group.reasons.map((r, i) => (
                        <div key={r.id} className={`flex items-center justify-between px-4 py-2.5 ${i < group.reasons.length - 1 ? 'border-b' : ''} hover:bg-gray-50 group`}>
                          <span className="text-sm text-gray-800">{r.reason}</span>
                          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              className="p-1 text-gray-400 hover:text-blue-600 rounded"
                              onClick={() => {
                                setReasonDialog({ mode: 'edit-reason', type: leadType, data: r });
                                setReasonForm({ category: group.category, reason: r.reason, old_name: '' });
                              }}
                            ><Pencil className="w-3.5 h-3.5" /></button>
                            <button
                              className="p-1 text-gray-400 hover:text-red-600 rounded"
                              onClick={() => setDeleteReasonTarget({ ...r, type: leadType })}
                            ><Trash2 className="w-3.5 h-3.5" /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ))
                )}
              </TabsContent>
            );
          })}
        </Tabs>
      </div>

      {/* Lead Reason Add/Edit/Rename Dialog */}
      <Dialog open={!!reasonDialog} onOpenChange={v => !v && setReasonDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {reasonDialog?.mode === 'add-reason' ? 'Add Reason' :
               reasonDialog?.mode === 'edit-reason' ? 'Edit Reason' : 'Rename Category'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {reasonDialog?.mode !== 'rename-category' && (
              <div className="space-y-1">
                <Label>Category</Label>
                <Input
                  value={reasonForm.category}
                  onChange={e => setReasonForm(f => ({...f, category: e.target.value}))}
                  placeholder="e.g. Pricing"
                  data-testid="reason-category-input"
                />
                <p className="text-xs text-gray-400">Type an existing category to add to it, or a new name to create one</p>
              </div>
            )}
            {reasonDialog?.mode === 'rename-category' && (
              <div className="space-y-1">
                <Label>New Category Name</Label>
                <Input
                  value={reasonForm.category}
                  onChange={e => setReasonForm(f => ({...f, category: e.target.value}))}
                  placeholder="New name"
                  data-testid="reason-category-rename-input"
                />
              </div>
            )}
            {reasonDialog?.mode !== 'rename-category' && (
              <div className="space-y-1">
                <Label>Reason</Label>
                <Input
                  value={reasonForm.reason}
                  onChange={e => setReasonForm(f => ({...f, reason: e.target.value}))}
                  placeholder="Enter reason text"
                  data-testid="reason-text-input"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReasonDialog(null)}>Cancel</Button>
            <Button onClick={handleSaveReason} className="bg-[#075E54] hover:bg-[#064E46]" data-testid="save-reason-btn">
              {reasonDialog?.mode === 'add-reason' ? 'Add' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Reason Confirmation */}
      <AlertDialog open={!!deleteReasonTarget} onOpenChange={v => !v && setDeleteReasonTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this reason?</AlertDialogTitle>
            <AlertDialogDescription>
              "{deleteReasonTarget?.reason}" will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteReason} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add {itemType}</DialogTitle>
            <DialogDescription>
              Enter the name for the new {itemType.toLowerCase()}.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="item-name">Name</Label>
            <Input
              id="item-name"
              value={itemName}
              onChange={(e) => setItemName(e.target.value)}
              placeholder={`Enter ${itemType.toLowerCase()} name`}
              className="mt-2"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddItem} className="bg-[#25D366] hover:bg-[#20BA5A]">
              Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit {itemType}</DialogTitle>
            <DialogDescription>
              Update the name of the {itemType.toLowerCase()}.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="edit-item-name">Name</Label>
            <Input
              id="edit-item-name"
              value={itemName}
              onChange={(e) => setItemName(e.target.value)}
              placeholder={`Enter ${itemType.toLowerCase()} name`}
              className="mt-2"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditItem} className="bg-[#25D366] hover:bg-[#20BA5A]">
              Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{deletingItem?.name}". This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteItem}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Catalog;
